import { useState, useEffect } from 'react';
import { ShieldCheckIcon, XMarkIcon, PencilSquareIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import LoadingSpinner from '../common/LoadingSpinner';

interface RoleDetailPanelProps {
  roleId: string | null;
  onClose: () => void;
  onEdit?: (role: any) => void;
  /** Display names for permission categories; unknown keys fall back to the raw key. */
  categoryLabels?: Record<string, string>;
  /** Show the role→group-access section (local node only). Cloud roles have no group access. */
  showGroupAccess?: boolean;
}

/**
 * Read-only role detail. Fetches the full role via the injected API (useApi().roles),
 * so it works against whichever backend the host app wired. The group-access section is
 * opt-in (showGroupAccess) because only the local node assigns groups to roles.
 */
export default function RoleDetailPanel({ roleId, onClose, onEdit, categoryLabels = {}, showGroupAccess = false }: RoleDetailPanelProps) {
  const { roles } = useApi();
  const [role, setRole] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roleId) return;
    setLoading(true);
    roles.get(roleId)
      .then(setRole)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [roleId, roles]);

  if (!roleId) return null;

  const grouped: Record<string, any[]> = {};
  if (role?.permissions) {
    for (const raw of role.permissions) {
      // Tolerate both shapes: a permission object {id,name,category} or a bare name string.
      const perm = typeof raw === 'string' ? { id: raw, name: raw } : raw;
      const cat = perm.category || perm.name?.split('.')[0] || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(perm);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 overlay-backdrop" onClick={onClose} />

      <div className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-xl border border-border bg-card shadow-modal mx-4">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="h-5 w-5 text-brand" />
            <h2 className="text-[16px] font-semibold text-foreground capitalize">
              {role?.name?.replace(/_/g, ' ') || 'Role Details'}
            </h2>
            {role?.is_system && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                System
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                onClick={() => onEdit(role)}
                className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-brand transition-colors"
                title="Edit role"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <LoadingSpinner />
          ) : !role ? (
            <p className="text-[13px] text-muted-foreground">Role not found.</p>
          ) : (
            <div className="space-y-5">
              <div>
                <p className="text-[13px] text-muted-foreground mb-3">
                  {role.description || 'No description provided.'}
                </p>
                <div className="flex items-center gap-4 text-[12px] text-muted-foreground">
                  <span>{role.permissions?.length || 0} permissions</span>
                  {showGroupAccess && <span>{role.group_access?.length || 0} groups</span>}
                  <span>{role.user_count || 0} users assigned</span>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-[13px] font-semibold text-foreground">Assigned Permissions</h3>
                {Object.keys(grouped).length === 0 ? (
                  <p className="text-[12px] text-muted-foreground">No permissions assigned.</p>
                ) : (
                  <div className="space-y-3">
                    {Object.keys(grouped).sort().map((cat) => (
                      <div key={cat}>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                          {categoryLabels[cat] || cat}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {grouped[cat].map((perm) => (
                            <span
                              key={perm.id}
                              className="inline-flex rounded-md bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand"
                            >
                              {perm.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {showGroupAccess && (
                <div className="space-y-3">
                  <h3 className="text-[13px] font-semibold text-foreground">Group Access</h3>
                  {!role.group_access || role.group_access.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground">No group access assigned. This role has access to all groups.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {role.group_access.map((group: any) => (
                        <span
                          key={group.id}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                        >
                          <FolderIcon className="h-3 w-3" />
                          {group.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
